import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  Button,
  Switch,
  HStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { ChromePicker } from "react-color";
import { cqAPI } from "../../lib/Auth";
import _ from "lodash";

const DisplayInfo = () => {
  const toast = useToast();
  const bgColor = useColorModeValue("gray.100", "white");
  const [settings, setSettings] = useState({
    bgcolor: "#ffffff",
    color: "#000000",
    enabled: false,
    show_location_selector: undefined, // may be true/false/undefined
  });
  const [originalSettings, setOriginalSettings] = useState({
    bgcolor: "#ffffff",
    color: "#000000",
    enabled: false,
    show_location_selector: undefined,
  });
  const [hasChanges, setHasChanges] = useState(false);

  const fetchSettings = async () => {
    try {
      const bgRes = await cqAPI.get("/settings/widget?id=bgcolor");
      const colorRes = await cqAPI.get("/settings/widget?id=color");
      const enabledRes = await cqAPI.get("/settings/widget?id=enabled");
      const showLocationSelector = await cqAPI.get(
        "/settings/widget?id=show_location_selector"
      );

      const settingsData = {
        bgcolor: bgRes.data,
        color: colorRes.data,
        enabled: enabledRes.data,
        show_location_selector: showLocationSelector.data, // true/false/undefined
      };

      setSettings(settingsData);
      setOriginalSettings(_.cloneDeep(settingsData));
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSwitchChange = (event) => {
    setSettings((prev) => ({
      ...prev,
      enabled: event.target.checked,
    }));
  };

  const handleBgColorChange = (color) => {
    setSettings((prev) => ({
      ...prev,
      bgcolor: color.hex,
    }));
  };

  const handleFontColorChange = (color) => {
    setSettings((prev) => ({
      ...prev,
      color: color.hex,
    }));
  };

  const handleShowLocationSelectorChange = (event) => {
    setSettings((prev) => ({
      ...prev,
      show_location_selector: event.target.checked,
    }));
  };

  useEffect(() => {
    const changesDetected = !_.isEqual(settings, originalSettings);
    setHasChanges(changesDetected);
  }, [settings, originalSettings]);

  const saveChanges = async () => {
    try {
      const changes = {};
      Object.keys(settings).forEach((key) => {
        if (!_.isEqual(settings[key], originalSettings[key])) {
          changes[key] = settings[key];
        }
      });

      const savePromises = Object.keys(changes).map((key) =>
        cqAPI.post("/settings/widget", { key, value: changes[key] })
      );

      await Promise.all(savePromises);
      setOriginalSettings(_.cloneDeep(settings));
      toast({
        title: "Settings saved",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Failed to save changes:", error);
      toast({
        title: "Failed to save changes",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box p={4} overflowY="auto" maxH="100vh" w="100%" align="left">
      <Box textAlign="right" mt={4} paddingBottom="25px">
        <Button colorScheme="blue" isDisabled={!hasChanges} onClick={saveChanges}>
          Save
        </Button>
      </Box>

      <Box p={4} overflowY="auto" maxH="100vh" w="100%" align="left">
        <Text mb={2}>Enabled:</Text>
        <Switch isChecked={settings.enabled} onChange={handleSwitchChange} mb={4} />

        <HStack spacing={8} mt={4}>
          <Box>
            <Text mb={2}>Background Color:</Text>
            <div className="color-picker">
              <ChromePicker color={settings.bgcolor} onChangeComplete={handleBgColorChange} />
            </div>
          </Box>
          <Box>
            <Text mb={2}>Font Color:</Text>
            <div className="color-picker">
              <ChromePicker color={settings.color} onChangeComplete={handleFontColorChange} />
            </div>
          </Box>
        </HStack>

        {/* Conditionally show only if defined */}
        {typeof settings.show_location_selector === "boolean" && (
          <Box mt={6}>
            <Text mb={2}>Show location selector:</Text>
            <Switch
              isChecked={settings.show_location_selector}
              onChange={handleShowLocationSelectorChange}
            />
          </Box>
        )}

        {/* Preview Section */}
        <Box mt={8}>
          <Text mb={2}>Sample:</Text>
          <Box
            p={4}
            borderWidth={1}
            borderColor={"black"}
            borderRadius="10px 10px 0 0"
            bg={settings.bgcolor}
            color={settings.color}
            w="420px"
            height="40px"
            display="flex"
            alignItems="center"
            textAlign="left"
            style={{ cursor: "pointer" }}
          >
            <Text fontWeight={"bold"} fontSize={"large"}>
              Have any questions? Ask us now!
            </Text>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DisplayInfo;
